(() => {
  if (customElements.get('real-bread-financing')) return;

  const sdkURL = 'https://connect.breadpayments.com/sdk.js';
  const placementIDs = ['bread-placement-1', 'bread-placement-2'];
  const scriptLoads = new WeakMap();
  let sdkPromise;
  let initializedSDK;
  let initialization;

  const waitForScript = (script) => {
    if (!scriptLoads.has(script)) {
      scriptLoads.set(script, new Promise((resolve, reject) => {
        const onLoad = () => finish();
        const onError = () => {
          if (script.src === sdkURL) script.remove();
          finish(new Error('Bread script could not be loaded.'));
        };
        const finish = (error) => {
          script.removeEventListener('load', onLoad);
          script.removeEventListener('error', onError);
          error ? reject(error) : resolve();
        };
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', onError, { once: true });
      }));
    }

    // A timeout does not cancel a download. Keep observing it so a section reload
    // can reuse a late success or replace a script that later reports an error.
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Bread script did not load.')), 15000);
      scriptLoads.get(script).then(
        () => { clearTimeout(timeout); resolve(); },
        (error) => { clearTimeout(timeout); reject(error); }
      );
    });
  };

  const loadSDK = () => {
    if (sdkPromise) return sdkPromise;

    sdkPromise = (async () => {
      // Shopify starts its app scripts at window load. Let Bread's app supply the SDK first.
      if (document.readyState !== 'complete') {
        await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
      }

      if (window.BreadPayments) return window.BreadPayments;

      const findSDK = () => [...document.scripts].find((script) => script.src === sdkURL);
      const appScript = [...document.scripts].find((script) =>
        script.src.includes('/api/shopify-plugin-backend/messaging-app/cart.js')
      );

      if (!findSDK() && appScript) {
        try {
          await waitForScript(appScript);
        } catch {
          // The financing page can supply the SDK if the app bootstrap fails.
        }
      }
      if (window.BreadPayments) return window.BreadPayments;

      let script = findSDK();
      if (script) {
        await waitForScript(script);
      } else {
        script = document.createElement('script');
        // Bread's app checks this ID before injecting its SDK if it arrives late.
        script.id = 'bread-cart-sdk-script';
        script.src = sdkURL;
        script.async = true;
        const loaded = waitForScript(script);
        document.head.append(script);
        await loaded;
      }

      if (!window.BreadPayments) {
        script.remove();
        throw new Error('Bread SDK is unavailable.');
      }
      return window.BreadPayments;
    })().catch((error) => {
      sdkPromise = undefined;
      throw error;
    });

    return sdkPromise;
  };

  customElements.define(
    'real-bread-financing',
    class extends HTMLElement {
      async connectedCallback() {
        try {
          const sdk = await loadSDK();
          if (!this.isConnected) return;

          if (initialization) await initialization;
          if (!this.isConnected) return;

          const placements = placementIDs.map((domID) => ({ allowCheckout: false, domID }));
          if (initializedSDK !== sdk) {
            sdk.setInitMode('manual');
            sdk.setup({
              integrationKey: this.dataset.integrationKey,
              onInitialized: () => { initializedSDK = sdk; },
            });
            sdk.on('INSTALLMENT:APPLICATION_CHECKOUT', () => {});
            sdk.on('INSTALLMENT:APPLICATION_DECISIONED', () => {});
            sdk.registerPlacements(placements);
            initialization = sdk.init();
            try {
              await initialization;
            } finally {
              initialization = undefined;
            }
          } else {
            // New section HTML needs fresh placements after a theme-editor reload.
            sdk.registerPlacements(placements);
          }
        } catch (error) {
          console.warn('[REAL Bread financing]', error);
        }
      }
    }
  );
})();
